import { memo, type ReactNode } from "react";
import type { ProColumns } from "@ant-design/pro-components";
import type { TableColumnConfig } from "@mijalowcode/types";
import { isPlainObject } from "./storage-search";

export type ProTableAreaNodesResolver = (
  areaName: string,
  payload?: Record<string, unknown>,
) => ReactNode[] | undefined;

export function buildProTableSlotRuntimeContext(
  runtimeContext: Record<string, unknown> | undefined,
  slotRuntimeContext: Record<string, unknown>,
): Record<string, unknown> {
  const baseData = isPlainObject(runtimeContext?.data)
    ? (runtimeContext.data as Record<string, unknown>)
    : {};
  const slotData = isPlainObject(slotRuntimeContext.data)
    ? (slotRuntimeContext.data as Record<string, unknown>)
    : {};

  return {
    ...(runtimeContext || {}),
    ...slotRuntimeContext,
    data: {
      ...baseData,
      ...slotData,
    },
  };
}

type ProTableSlotCellProps = {
  areaNames: string[];
  column: TableColumnConfig;
  getAreaNodes?: ProTableAreaNodesResolver;
  index: number;
  record: Record<string, unknown>;
  runtimeContext?: Record<string, unknown>;
  value: unknown;
  valueEnum?: ProColumns["valueEnum"];
};

export const ProTableSlotCell = memo(
  function ProTableSlotCell({
    areaNames,
    column,
    getAreaNodes,
    index,
    record,
    runtimeContext,
    value,
    valueEnum,
  }: ProTableSlotCellProps) {
    const enumRecord = valueEnum as
      | Record<string, { text?: unknown; status?: unknown }>
      | undefined;
    const enumMatch =
      enumRecord && value !== undefined && value !== null
        ? enumRecord[String(value)]
        : undefined;
    const slotPayload = {
      record,
      index,
      column,
      value,
      valueEnum,
      valueEnumItem: enumMatch,
      enumText: enumMatch?.text,
      enumStatus: enumMatch?.status,
    };
    const slotNodes = areaNames.reduce<ReactNode[] | undefined>(
      (matchedNodes, areaName) => {
        if (matchedNodes !== undefined) {
          return matchedNodes;
        }
        const slotRuntimeContext = buildProTableSlotRuntimeContext(
          runtimeContext,
          {
            scope: "table.cell",
            area: areaName,
            container: {
              type: "table",
              area: areaName,
              index,
              column,
              record,
            },
            data: {
              ...slotPayload,
              rawValue: value,
              displayValue:
                enumMatch?.text !== undefined && enumMatch?.text !== null
                  ? enumMatch.text
                  : value,
              status: enumMatch?.status,
            },
          },
        );
        return getAreaNodes?.(areaName, {
          ...slotPayload,
          runtimeContext: slotRuntimeContext,
        });
      },
      undefined,
    );
    if (slotNodes !== undefined) {
      return slotNodes.length === 1 ? slotNodes[0] : slotNodes;
    }
    return String(record[column.dataIndex] ?? "");
  },
  (prev, next) =>
    prev.areaNames === next.areaNames &&
    prev.column === next.column &&
    prev.getAreaNodes === next.getAreaNodes &&
    prev.index === next.index &&
    prev.record === next.record &&
    prev.runtimeContext === next.runtimeContext &&
    prev.value === next.value &&
    prev.valueEnum === next.valueEnum,
);
